import { CSSProperties, ReactNode } from 'react';
import useScreen from 'Hooks/useScreen';
import { clsx, styx } from "modules/Utils";
import Colors from 'modules/Colors';
import Sty from 'modules/Sty';
import Icon from './Icon';
import Loader from './Loader';

interface CardSymphonyProps {
    id?: string;
    className: string;
    style?: CSSProperties;
    title?: string | ReactNode;
    subtitle?: string | ReactNode;
    titleTextPadding?: string,
    additionalButtons?: JSX.Element,
    children?: ReactNode | ReactNode[] | JSX.Element | string | number | null | undefined
    size?: 'small' | 'large'
    isNoPadding?: boolean
    isPadding16?: boolean
    isRow?: boolean
    isGemIcon?: boolean
    isLoading?: boolean
}

const CardSymphony = ({
    id,
    className,
    style,
    title,
    subtitle,
    additionalButtons,
    children,
    isNoPadding = false,
    size = 'large',
    isPadding16 = false,
    isRow = false,
    isGemIcon = false,
    isLoading = false
}: CardSymphonyProps) => {
    const { mStyle } = useScreen()

    return (
        <div
            id={id}
            className={clsx('cardSymphonyElement', className)}
            style={styx(
                mStyle(sty, 'cardSymphonyElement'),
                isPadding16 && { paddingLeft: 16, paddingRight: 16, paddingTop: 16, paddingBottom: 16 },
                isNoPadding && { paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0 },
                style
            )}
        >
            {(title || subtitle) &&
                <div
                    className="titlesContainer"
                    style={styx(
                        mStyle(sty, 'titlesContainer'),
                        sty[`${size}_titlesContainer`],
                        isRow && { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }
                    )}
                >
                    <div
                        className="title"
                        style={styx(
                            mStyle(sty, 'title'),
                            sty[`${size}_title`]
                        )}
                    >
                        {isGemIcon ?
                            <div className="gemIconCardContainer" style={sty.gemIconCardContainer}>
                                <div className="gemIconTitle" style={Sty.text18bold}>{title}</div>
                                <Icon className="gemIcon" icon="gem" size={22} color={Colors.purple} />
                            </div>
                            :
                            title
                        }
                        {isLoading &&
                            <Loader
                                className="titleLoader"
                                size={28}
                            />
                        }
                    </div>
                    {subtitle &&
                        <div className="subtitle" style={styx(
                            mStyle(sty, 'subtitle'),
                            sty[`${size}_subtitle`]
                        )}>
                            {subtitle}
                        </div>
                    }
                </div>
            }
            {children && children}
            {additionalButtons &&
                <div className="additionalButtonsContainer" style={{ width: '100%' }}>
                    {additionalButtons}
                </div>
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    cardSymphonyElement: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: 'white',
        paddingTop: 24,
        paddingBottom: 24,
        paddingLeft: 24,
        paddingRight: 24,
        borderRadius: 8,
        gap: 16
    },
    cardSymphonyElement_mobile: {
        paddingTop: 16,
        paddingBottom: 16,
        paddingLeft: 16,
        paddingRight: 16,
        borderRadius: 0,
    },
    padding16: {
        paddingLeft: 16,
        paddingRight: 16,
        paddingTop: 16,
        paddingBottom: 16,
    },
    headerChildContainerDesktop: {
        borderRadius: 8,
    },
    headerChildContainerMobile: {
        borderRadius: 0,
        borderBottom: Sty.border,
    },
    titlesContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 10,
        width: '100%'
    },
    small_titlesContainer: {
        gap: 0,
    },
    titlesContainer_mobile: {
        gap: 4,
    },

    title: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        width: '100%',
        fontSize: 28,
    },
    title_mobile: {
        fontSize: 28,
    },
    subtitle: {
        fontSize: 18,
        color: Colors.greyDark
    },
    subtitle_mobile: {
        fontSize: 17,
    },

    small_title: {
        ...Sty.text18bold,
    },
    small_subtitle: {
        ...Sty.text16grey,
    },
    gemIconCardContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
}

export default CardSymphony;